import { css } from '@emotion/react'
import { Cell, DropdownItem, Grid, HFlow, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { DateTime } from 'components/date'
import { DropdownButton } from 'components/dropdown'
import { CheckboxField, FormDebouncedValueSpy, SubmitlessForm, TextField } from 'components/form'
import { HLabel } from 'components/HLabel'
import { Cnpj, Cpf } from 'components/label'
import { confirm } from 'components/modals/confirm'
import { TableBox, usePagedTableProps } from 'components/table'
import { useInativarCredencialIntegracaoMutation } from 'graphql/hooks.generated'
import {
  CredenciaisIntegracaoQuery,
  CredencialIntegracaoQueryInput,
  PageParams,
  TipoPessoaEnum,
} from 'graphql/types.generated'
import { metaPath } from 'util/metaPath'
import { stringGrouper } from 'util/string/string-grouper'

import { getDescricaoRecursoIntegracao } from './model-credencial-integracao'

type CredenciaisIntegracaoQueryModel = CredenciaisIntegracaoQuery['credenciaisIntegracao']['content'][0]

interface CredenciaisIntegracaoTableProps {
  readonly data: CredenciaisIntegracaoQuery
  readonly loading: boolean
  readonly setFilter: (filter: CredencialIntegracaoQueryInput) => void
  readonly refetch: () => void
}

const NAO_INFORMADO = 'Não informado'

const buildMunicipiosLabelValue = (municipios: CredenciaisIntegracaoQueryModel['municipiosVinculados']) => {
  if (municipios?.isNotEmpty()) {
    return stringGrouper(...municipios.map((municipio) => `${municipio.nome.titleCase()}/${municipio.uf.sigla}`))
  }
  return NAO_INFORMADO
}

export function CredenciaisIntegracaoTable(props: CredenciaisIntegracaoTableProps) {
  const { data, loading, refetch, setFilter } = props
  const theme = useTheme()
  const styles = createStyles(theme)

  const [inativarCredencialIntegracao] = useInativarCredencialIntegracaoMutation()

  const tableProps = usePagedTableProps<
    CredenciaisIntegracaoQuery['credenciaisIntegracao'],
    { pageParams?: PageParams }
  >({
    loading,
    result: data?.credenciaisIntegracao,
    onChange: setFilter,
  })

  const onInativarCredencial = (item: CredenciaisIntegracaoQueryModel) =>
    confirm({
      title: 'Deseja inativar a credencial?',
      body: <InativarCredencialModalBody item={item} />,
      type: 'danger',
      confirmLabel: 'Inativar',
      onConfirm: () =>
        inativarCredencialIntegracao({ variables: { id: item.id } }).then(() => {
          refetch()
        }),
    })()

  const renderAccordionPanel = (item: CredenciaisIntegracaoQueryModel) => (
    <Grid css={styles.accordionPanel}>
      <Cell size={12}>
        <VFlow style={{ padding: '1rem 0rem' }} vSpacing={0.5}>
          <HLabel title='URL:'>
            <Text>{item.urlWebhookRegulacao ?? NAO_INFORMADO}</Text>
          </HLabel>
          <HLabel title='Municípios vinculados:'>
            <Text>
              {item.municipiosVinculados?.isNotEmpty()
                ? buildMunicipiosLabelValue(item.municipiosVinculados)
                : NAO_INFORMADO}
            </Text>
          </HLabel>
        </VFlow>
      </Cell>
    </Grid>
  )

  return (
    <TableBox header={<TableHeader onChange={setFilter} />}>
      <AccordionDataTable<CredenciaisIntegracaoQueryModel>
        components={{ AccordionPanel: (item) => renderAccordionPanel(item.row) }}
        loading={loading}
        columns={[
          {
            name: 'data',
            header: 'Data',
            render: (item) => <DateTime value={item.dataCriacao} />,
            style: styles.dataPanel,
            size: 1,
          },
          {
            name: 'responsavel',
            header: 'Responsável',
            render: renderResponsavel,
            size: 3,
          },
          {
            name: 'recursos',
            header: 'Tipo de credencial',
            render: renderRecursos,
            size: 5,
          },
          {
            name: 'nomeCredencial',
            header: 'Nome da credencial',
            render: (item) => <Text>{item.nomeCredencial}</Text>,
            size: 3,
          },
          {
            name: 'status',
            header: 'Status',
            render: renderStatus,
            size: 2,
          },
          {
            name: 'actions',
            render: (item) => (
              <DropdownButton>
                <DropdownItem onClick={() => onInativarCredencial(item)} disabled={!item.ativo}>
                  Inativar
                </DropdownItem>
              </DropdownButton>
            ),
            size: 1,
          },
        ]}
        {...tableProps}
      />
    </TableBox>
  )
}

const filterPath = metaPath<CredencialIntegracaoQueryInput>()

const TableHeader = ({ onChange }: { onChange: (values: CredencialIntegracaoQueryInput) => void }) => {
  return (
    <SubmitlessForm
      render={() => (
        <form noValidate>
          <FormDebouncedValueSpy onChange={({ values }) => onChange(values)} />
          <Grid gap={1}>
            <Cell size={5} alignSelf='center'>
              <TextField
                name={filterPath.query}
                icon='zoomOutline'
                placeholder='Pesquise por responsável, CPF, CNPJ ou nome da credencial'
              />
            </Cell>
            <Cell size={7} alignSelf='center'>
              <CheckboxField name={filterPath.somenteAtivas} label='Exibir somente credenciais ativas' />
            </Cell>
          </Grid>
        </form>
      )}
    />
  )
}

const InativarCredencialModalBody = ({ item }: { item: CredenciaisIntegracaoQueryModel }) => (
  <VFlow>
    <Text>
      Ao inativar a credencial a ação não poderá ser desfeita. Caso necessário, após a inativação gere uma nova
      credencial.
    </Text>
    <VFlow vSpacing={0}>
      <HLabel title='Nome da credencial'>{item.nomeCredencial}</HLabel>
      <HLabel title='Responsável'>{item.nomeResponsavel}</HLabel>
      {item.tipoPessoa === TipoPessoaEnum.FISICA ? (
        <HLabel title='CPF'>
          <Cpf value={item.cpfCnpj} />
        </HLabel>
      ) : (
        <HLabel title='CNPJ'>
          <Cnpj value={item.cpfCnpj} />
        </HLabel>
      )}
    </VFlow>
  </VFlow>
)

const renderResponsavel = (item: CredenciaisIntegracaoQueryModel) => (
  <VFlow vSpacing={0} style={{ margin: '0.5rem 0' }}>
    <Text>{item.nomeResponsavel}</Text>
    {item.tipoPessoa === TipoPessoaEnum.FISICA ? (
      <HLabel title='CPF'>
        <Cpf value={item.cpfCnpj} />
      </HLabel>
    ) : (
      <HLabel title='CNPJ'>
        <Cnpj value={item.cpfCnpj} />
      </HLabel>
    )}
    <Text>{item.email}</Text>
  </VFlow>
)

const renderStatus = ({ ativo }: CredenciaisIntegracaoQueryModel) => (
  <HFlow hSpacing={0.25} alignItems='center'>
    <Icon size={1} fill={ativo ? 'success' : 'danger'} icon={ativo ? 'checkCircleOutline' : 'banOutline'} />
    <Text color={ativo ? 'success' : 'danger'}>{ativo ? 'Ativo' : 'Inativo'}</Text>
  </HFlow>
)

const renderRecursos = (item: CredenciaisIntegracaoQueryModel) => (
  <VFlow vSpacing={0} style={{ margin: '0.5rem 0' }}>
    <Text>{stringGrouper(...item.recursos.map((recurso) => getDescricaoRecursoIntegracao(recurso)))}</Text>
  </VFlow>
)

const createStyles = (theme: Theme) => ({
  accordionPanel: css`
    background: ${theme.pallete.gray.c90};
  `,
  dataPanel: css`
    min-width: 6.5rem;
    max-width: 6.5rem;
    width: 6.5rem;
  `,
})
